// @flow

import React, { type ComponentType } from "react";
import { connect } from "react-redux";
import * as accountSelectors from "@modules/account/selectors";
import type { State } from "@store/reducers";
import AccessRestricted from "./access-restricted";
import { getClientSettings } from "@modules/settings/selectors";

export type PropsFromPermissions = $Exact<{
  hasAccess: (permission: string) => boolean,
  hasPermission: (permissionId: string) => boolean,
  hasContextSwitch: boolean,
  hasReportingContextSwitch: boolean,
}>;

type PropsFromState = $Exact<{
  permissions: { [permission: string]: boolean } | null,
  hasContextSwitch: boolean,
  hasReportingContextSwitch: boolean,
  clientSettings: Object,
}>;

type OwnProps = $Exact<{
  permissionId?: string,
  getNavigation?: Function,
  clientPermission?: string,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
}>;

const withPermissions = (props: OwnProps) => {
  return (WrappedComponent: ComponentType<any>) => {
    class WithPermissions extends React.Component<Props> {
      get sectionName(): string {
        return props.permissionId
          ? props.permissionId.substring(props.permissionId.lastIndexOf(".") + 1)
          : "";
      }

      get displayName(): string {
        return this.sectionName.charAt(0).toUpperCase() + this.sectionName.slice(1);
      }

      get permissions(): { [permission: string]: boolean } {
        return this.props.permissions || {};
      }

      hasAccess = (permissionId: string) =>
        (permissionId.startsWith("app") || permissionId.startsWith("privilege")) &&
        Object.keys(this.permissions).some(s => s.startsWith(permissionId));

      hasPermission = (permissionId: string) =>
        permissionId === "" ? true : this.permissions[permissionId];

      hasClientAccess = () => {
        if (props.clientPermission && this.props.clientSettings) {
          return this.props.clientSettings[props.clientPermission];
        }
        return true;
      };

      render() {
        if (!props.permissionId || (this.hasAccess(props.permissionId) && this.hasClientAccess())) {
          return (
            <WrappedComponent
              {...this.props}
              hasAccess={this.hasAccess}
              hasPermission={this.hasPermission}
              hasContextSwitch={this.props.hasContextSwitch}
              hasReportingContextSwitch={this.props.hasReportingContextSwitch}
            />
          );
        }

        return (
          <AccessRestricted
            getNavigation={props.getNavigation}
            hasPermission={this.hasPermission}
            sectionName={this.displayName}
            hideSidebar={false}
          />
        );
      }
    }

    const mapStateToProps = (state: State): PropsFromState => {
      return {
        permissions: accountSelectors.getPermissions(state),
        hasContextSwitch: accountSelectors.hasClientContextSwitch(state),
        hasReportingContextSwitch: accountSelectors.hasReportingClientContextSwitch(state),
        clientSettings: getClientSettings(state),
      };
    };

    return connect(mapStateToProps)(WithPermissions);
  };
};

export default withPermissions;
